import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { groupApi, appApi } from '../lib/api';
import {
  Plus, Pencil, Trash2, X, Users, History, ChevronDown,
  AlertTriangle, CheckCircle2, UserPlus, UserMinus, Settings, Trash,
  Eye, EyeOff,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/* ── Types ─────────────────────────────────────────────────────── */
interface GroupForm { groupName: string; description: string; precedence: string }
const emptyForm: GroupForm = { groupName: '', description: '', precedence: '' };

interface GroupMember { username: string; email: string }
interface HistoryEvent { action: string; performedBy: string; details: Record<string, any>; timestamp: string }

/* ── Action label / icon helpers ────────────────────────────────── */
function actionLabel(action: string) {
  const map: Record<string, string> = {
    GROUP_CREATED:  'Grupo creado',
    GROUP_UPDATED:  'Grupo actualizado',
    GROUP_DELETED:  'Grupo eliminado',
    MEMBER_ADDED:   'Miembro agregado',
    MEMBER_REMOVED: 'Miembro eliminado',
  };
  return map[action] ?? action;
}

function ActionIcon({ action }: { action: string }) {
  const cls = 'h-4 w-4 shrink-0';
  if (action === 'MEMBER_ADDED')   return <UserPlus  className={cn(cls, 'text-success')} />;
  if (action === 'MEMBER_REMOVED') return <UserMinus className={cn(cls, 'text-destructive')} />;
  if (action === 'GROUP_CREATED')  return <CheckCircle2 className={cn(cls, 'text-accent')} />;
  if (action === 'GROUP_UPDATED')  return <Settings  className={cn(cls, 'text-warning')} />;
  if (action === 'GROUP_DELETED')  return <Trash     className={cn(cls, 'text-destructive')} />;
  return <History className={cn(cls, 'text-muted-foreground')} />;
}

/* ── Main component ─────────────────────────────────────────────── */
export default function Groups() {
  const queryClient = useQueryClient();

  // Form modal
  const [showForm, setShowForm]     = useState(false);
  const [editGroup, setEditGroup]   = useState<any | null>(null);
  const [form, setForm]             = useState<GroupForm>(emptyForm);
  const [formError, setFormError]   = useState('');

  // Filter
  const [appFilter, setAppFilter]   = useState('');

  // Modals
  const [membersGroup, setMembersGroup]   = useState<string | null>(null);
  const [historyGroup, setHistoryGroup]   = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget]   = useState<any | null>(null);
  const [showDeleted, setShowDeleted]     = useState(false);
  const [deletedHistory, setDeletedHistory] = useState<string | null>(null);

  /* ── Queries ─────────────────────────────────────────────────── */
  const { data: groups = [], isLoading: groupsLoading } = useQuery({ queryKey: ['groups'], queryFn: groupApi.list });
  const { data: apps = [] } = useQuery({ queryKey: ['apps'], queryFn: appApi.list });

  const { data: members = [], isFetching: membersLoading } = useQuery({
    queryKey: ['group-members', membersGroup],
    queryFn: () => groupApi.listMembers(membersGroup!),
    enabled: !!membersGroup,
  });

  const { data: deleteMembers = [], isFetching: deleteMembersLoading } = useQuery({
    queryKey: ['group-members', deleteTarget?.groupName],
    queryFn: () => groupApi.listMembers(deleteTarget!.groupName),
    enabled: !!deleteTarget,
  });

  const memberLabel = (m: GroupMember) => m.email || m.username;

  const { data: historyEvents = [], isFetching: historyLoading } = useQuery({
    queryKey: ['group-history', historyGroup],
    queryFn: () => groupApi.getHistory(historyGroup!),
    enabled: !!historyGroup,
  });

  const { data: deletedGroups = [], isLoading: deletedLoading } = useQuery({
    queryKey: ['groups-deleted'],
    queryFn: groupApi.listDeleted,
    enabled: showDeleted,
  });

  const { data: deletedDetail, isFetching: deletedDetailLoading } = useQuery({
    queryKey: ['group-deleted-detail', deletedHistory],
    queryFn: () => groupApi.getDeleted(deletedHistory!),
    enabled: !!deletedHistory,
  });

  /* ── Mutations ───────────────────────────────────────────────── */
  const createMutation = useMutation({
    mutationFn: groupApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      setShowForm(false); setForm(emptyForm); setFormError('');
    },
    onError: (e: any) => setFormError(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ groupName, data }: { groupName: string; data: any }) => groupApi.update(groupName, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      setEditGroup(null); setShowForm(false); setForm(emptyForm); setFormError('');
    },
    onError: (e: any) => setFormError(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: groupApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['groups-deleted'] });
      setDeleteTarget(null);
    },
    onError: (e: any) => setFormError(e.message),
  });

  /* ── Helpers ─────────────────────────────────────────────────── */
  const openCreate = () => { setEditGroup(null); setForm(emptyForm); setFormError(''); setShowForm(true); };
  const openEdit = (g: any) => {
    setEditGroup(g);
    setForm({ groupName: g.groupName, description: g.description || '', precedence: g.precedence !== undefined ? String(g.precedence) : '' });
    setFormError(''); setShowForm(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data = { description: form.description || undefined, precedence: form.precedence !== '' ? parseInt(form.precedence, 10) : undefined };
    if (editGroup) updateMutation.mutate({ groupName: editGroup.groupName, data });
    else createMutation.mutate({ groupName: form.groupName, ...data });
  };

  // Map cognitoGroupName → app names
  const groupToApps = (apps as any[]).reduce<Record<string, string[]>>((acc, app) => {
    for (const level of app.accessLevels ?? []) {
      if (!acc[level.cognitoGroupName]) acc[level.cognitoGroupName] = [];
      acc[level.cognitoGroupName].push(app.name);
    }
    return acc;
  }, {});

  // Groups of selected app filter
  const appGroupNames: Set<string> | null = appFilter
    ? new Set(((apps as any[]).find(a => a.appId === appFilter)?.accessLevels ?? []).map((l: any) => l.cognitoGroupName))
    : null;

  const filtered = appGroupNames
    ? (groups as any[]).filter(g => appGroupNames.has(g.groupName))
    : (groups as any[]);

  // Apps that use the group being deleted
  const deletingApps: any[] = deleteTarget ? (groupToApps[deleteTarget.groupName] ?? []) : [];

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-foreground">Grupos de Cognito</h1>
          <p className="mt-1 text-sm text-muted-foreground">Grupos del User Pool. Definen los niveles de acceso a las aplicaciones.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDeleted(v => !v)}
            className={cn(btnSecondary, 'gap-2', showDeleted && 'bg-muted')}
          >
            {showDeleted ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showDeleted ? 'Ocultar eliminados' : 'Ver eliminados'}
          </button>
          <button onClick={openCreate} className={btnPrimary}>
            <Plus className="h-4 w-4 mr-2" />Nuevo grupo
          </button>
        </div>
      </div>

      {/* ── Filter ── */}
      <div className="flex items-center gap-3">
        <div className="relative">
          <select value={appFilter} onChange={e => setAppFilter(e.target.value)} className={cn(selectCls, 'pr-8 appearance-none')}>
            <option value="">Todas las apps</option>
            {(apps as any[]).map(app => <option key={app.appId} value={app.appId}>{app.name}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        </div>
        {appFilter && (
          <button onClick={() => setAppFilter('')} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
            <X className="h-3 w-3" /> Limpiar
          </button>
        )}
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} grupo{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* ── Active groups table ── */}
      <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/40">
            <tr>
              <th className={th}>Nombre</th>
              <th className={th}>App</th>
              <th className={th}>Descripción</th>
              <th className={th}>Prec.</th>
              <th className={th}>Creado</th>
              <th className={cn(th, 'text-right')}>Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {groupsLoading ? (
              <tr><td colSpan={6} className={tdEmpty}>Cargando...</td></tr>
            ) : !filtered.length ? (
              <tr><td colSpan={6} className={tdEmpty}>No hay grupos</td></tr>
            ) : filtered.map((group: any) => (
              <tr key={group.groupName} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-mono font-medium text-foreground">{group.groupName}</td>
                <td className="px-4 py-3 text-sm">
                  {groupToApps[group.groupName]?.length ? (
                    <div className="flex flex-wrap gap-1">
                      {groupToApps[group.groupName].map(n => (
                        <span key={n} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/20">{n}</span>
                      ))}
                    </div>
                  ) : <span className="text-muted-foreground/40">—</span>}
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground max-w-xs truncate">{group.description || <span className="text-muted-foreground/40">—</span>}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground text-center">{group.precedence ?? <span className="text-muted-foreground/40">—</span>}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground">
                  {group.createdAt ? new Date(group.createdAt).toLocaleDateString('es-AR') : '—'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  <div className="flex justify-end items-center gap-1">
                    <button onClick={() => setMembersGroup(group.groupName)} title="Ver miembros" className={iconBtn}><Users className="h-4 w-4" /></button>
                    <button onClick={() => setHistoryGroup(group.groupName)} title="Ver historial" className={iconBtn}><History className="h-4 w-4" /></button>
                    <button onClick={() => openEdit(group)} title="Editar" className={iconBtn}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => setDeleteTarget(group)} title="Eliminar" className={cn(iconBtn, 'hover:text-destructive hover:bg-destructive/10')}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Deleted groups section ── */}
      {showDeleted && (
        <div className="space-y-3">
          <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
            <Trash className="h-4 w-4 text-muted-foreground" /> Grupos eliminados
          </h2>
          <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/40">
                <tr>
                  <th className={th}>Nombre</th>
                  <th className={th}>Descripción</th>
                  <th className={th}>Eliminado</th>
                  <th className={th}>Por</th>
                  <th className={th}>Miembros al eliminar</th>
                  <th className={cn(th, 'text-right')}>Historial</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {deletedLoading ? (
                  <tr><td colSpan={6} className={tdEmpty}>Cargando...</td></tr>
                ) : !(deletedGroups as any[]).length ? (
                  <tr><td colSpan={6} className={tdEmpty}>No hay grupos eliminados</td></tr>
                ) : (deletedGroups as any[]).map((g: any) => (
                  <tr key={g.groupName} className="hover:bg-muted/30 transition-colors opacity-75">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-muted-foreground line-through">{g.groupName}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground max-w-xs truncate">{g.description || <span className="text-muted-foreground/40">—</span>}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground">{new Date(g.deletedAt).toLocaleDateString('es-AR')}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground truncate max-w-[160px]">{g.deletedBy}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      <span className="inline-flex items-center gap-1" title={(g.membersSnapshot ?? []).map((m: GroupMember) => m.email || m.username).join(', ')}>
                        <Users className="h-3.5 w-3.5" />{g.membersSnapshot?.length ?? 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setDeletedHistory(g.groupName)} title="Ver historial" className={iconBtn}><History className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Members modal ── */}
      {membersGroup && (
        <Modal title="Miembros del grupo" subtitle={membersGroup} onClose={() => setMembersGroup(null)}>
          <div className="max-h-72 overflow-y-auto px-5 py-4">
            {membersLoading ? <ModalEmpty>Cargando...</ModalEmpty>
              : !(members as GroupMember[]).length ? <ModalEmpty>Este grupo no tiene miembros.</ModalEmpty>
              : <ul className="space-y-1.5">
                  {(members as GroupMember[]).map(m => (
                    <li key={m.username} className="flex items-center gap-2 text-sm px-3 py-2 rounded-md bg-muted/40">
                      <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <Link to={`/users/${encodeURIComponent(m.username)}`} className="text-foreground hover:text-accent truncate">{memberLabel(m)}</Link>
                    </li>
                  ))}
                </ul>
            }
          </div>
          <ModalFooter onClose={() => setMembersGroup(null)} label={`${(members as GroupMember[]).length} miembro${(members as GroupMember[]).length !== 1 ? 's' : ''}`} />
        </Modal>
      )}

      {/* ── History modal (active group) ── */}
      {historyGroup && (
        <Modal title="Historial del grupo" subtitle={historyGroup} onClose={() => setHistoryGroup(null)}>
          <HistoryList events={historyEvents as HistoryEvent[]} loading={historyLoading} />
          <ModalFooter onClose={() => setHistoryGroup(null)} label={`${(historyEvents as HistoryEvent[]).length} evento${(historyEvents as HistoryEvent[]).length !== 1 ? 's' : ''}`} />
        </Modal>
      )}

      {/* ── History modal (deleted group) ── */}
      {deletedHistory && (
        <Modal title="Historial del grupo eliminado" subtitle={deletedHistory} onClose={() => setDeletedHistory(null)}>
          {deletedDetailLoading ? <div className="px-5 py-8 text-center text-sm text-muted-foreground">Cargando...</div> : (
            <>
              {deletedDetail?.membersSnapshot?.length > 0 && (
                <div className="px-5 pt-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Miembros al momento de eliminación</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {deletedDetail.membersSnapshot.map((m: GroupMember) => (
                      <span key={m.username} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">
                        <Users className="h-3 w-3" />{m.email || m.username}
                      </span>
                    ))}
                  </div>
                  <div className="border-t border-border mb-2" />
                </div>
              )}
              <HistoryList events={(deletedDetail?.history ?? []) as HistoryEvent[]} loading={false} />
            </>
          )}
          <ModalFooter onClose={() => setDeletedHistory(null)} />
        </Modal>
      )}

      {/* ── Delete confirmation modal ── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !deleteMutation.isPending && setDeleteTarget(null)} />
          <div className="relative bg-card border border-border rounded-lg shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                <Trash2 className="h-4 w-4 text-destructive" /> Eliminar grupo
              </h3>
              {!deleteMutation.isPending && (
                <button onClick={() => setDeleteTarget(null)} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
              )}
            </div>

            <div className="px-5 py-4 space-y-4">
              <p className="text-sm text-foreground">
                ¿Eliminás el grupo <span className="font-mono font-semibold">{deleteTarget.groupName}</span>?
                <br /><span className="text-muted-foreground text-xs">Esta acción no se puede deshacer pero quedará registro histórico.</span>
              </p>

              {/* Blocked by apps */}
              {deletingApps.length > 0 && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 flex gap-3">
                  <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                  <div className="text-sm text-destructive">
                    <p className="font-semibold mb-1">No se puede eliminar</p>
                    <p>Este grupo está asignado a las siguientes apps:</p>
                    <ul className="mt-1 list-disc list-inside">
                      {deletingApps.map(n => <li key={n}>{n}</li>)}
                    </ul>
                    <p className="mt-1">Desasignalo desde Aplicaciones primero.</p>
                  </div>
                </div>
              )}

              {/* Members at time of delete */}
              {deletingApps.length === 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Miembros actuales</p>
                  {deleteMembersLoading ? (
                    <p className="text-xs text-muted-foreground">Cargando miembros...</p>
                  ) : !(deleteMembers as GroupMember[]).length ? (
                    <p className="text-xs text-muted-foreground italic">Sin miembros</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {(deleteMembers as GroupMember[]).map(m => (
                        <span key={m.username} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">
                          <Users className="h-3 w-3" />{m.email || m.username}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-border flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending} className={btnSecondary}>Cancelar</button>
              <button
                disabled={deletingApps.length > 0 || deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(deleteTarget.groupName)}
                className={cn(btnDanger, (deletingApps.length > 0 || deleteMutation.isPending) && 'opacity-50 cursor-not-allowed')}
              >
                {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar grupo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create / Edit modal ── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowForm(false)} />
          <div className="relative bg-card border border-border rounded-lg shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-foreground">{editGroup ? `Editar: ${editGroup.groupName}` : 'Nuevo Grupo'}</h3>
              <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
            </div>
            {formError && <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2 mb-4">{formError}</p>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Nombre del grupo *</label>
                <input required value={form.groupName} disabled={!!editGroup}
                  onChange={e => setForm({ ...form, groupName: e.target.value })}
                  className={cn(inputCls, editGroup && 'opacity-60 cursor-not-allowed', 'font-mono')}
                  placeholder="ej. parking-system-access" />
                {!editGroup && <p className="text-xs text-muted-foreground mt-1">Solo letras, números, guiones y underscores.</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Descripción</label>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className={inputCls} placeholder="Opcional" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Precedencia</label>
                <input type="number" min="0" value={form.precedence} onChange={e => setForm({ ...form, precedence: e.target.value })} className={inputCls} placeholder="Entero >= 0 (opcional)" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className={btnSecondary}>Cancelar</button>
                <button type="submit" disabled={createMutation.isPending || updateMutation.isPending} className={btnPrimary}>
                  {(createMutation.isPending || updateMutation.isPending) ? 'Guardando...' : editGroup ? 'Guardar' : 'Crear grupo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Sub-components ───────────────────────────────────────────── */

function Modal({ title, subtitle, onClose, children }: { title: string; subtitle?: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div>
            <h3 className="text-base font-semibold text-foreground">{title}</h3>
            {subtitle && <p className="text-xs font-mono text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function ModalEmpty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground text-center py-8">{children}</p>;
}

function ModalFooter({ onClose, label }: { onClose: () => void; label?: string }) {
  return (
    <div className="px-5 py-3 border-t border-border flex justify-between items-center shrink-0">
      <span className="text-xs text-muted-foreground">{label ?? ''}</span>
      <button onClick={onClose} className={btnSecondary}>Cerrar</button>
    </div>
  );
}

function HistoryList({ events, loading }: { events: HistoryEvent[]; loading: boolean }) {
  if (loading) return <div className="px-5 py-8 text-center text-sm text-muted-foreground">Cargando...</div>;
  if (!events.length) return <div className="px-5 py-8 text-center text-sm text-muted-foreground">Sin eventos registrados.</div>;
  return (
    <ul className="px-5 py-4 space-y-3">
      {events.map((ev, i) => (
        <li key={i} className="flex gap-3 text-sm">
          <div className="flex flex-col items-center gap-1">
            <ActionIcon action={ev.action} />
            {i < events.length - 1 && <div className="w-px flex-1 bg-border" />}
          </div>
          <div className="flex-1 pb-3">
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-medium text-foreground">{actionLabel(ev.action)}</span>
              <span className="text-xs text-muted-foreground shrink-0">{new Date(ev.timestamp).toLocaleString('es-AR')}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">Por: {ev.performedBy}</p>
            {Object.keys(ev.details ?? {}).length > 0 && (
              <div className="mt-1 text-xs text-muted-foreground space-y-0.5">
                {ev.details.username && <p>Usuario: <span className="font-mono">{ev.details.username}</span></p>}
                {ev.details.email && ev.details.email !== ev.details.username && <p>Email: {ev.details.email}</p>}
                {ev.details.description !== undefined && <p>Descripción: {ev.details.description || '—'}</p>}
                {ev.details.memberCount !== undefined && <p>Miembros al eliminar: {ev.details.memberCount}</p>}
              </div>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ── Styles ───────────────────────────────────────────────────── */
const th = 'px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider';
const tdEmpty = 'px-4 py-10 text-center text-sm text-muted-foreground';
const iconBtn = 'p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors';
const inputCls = 'block w-full border border-input bg-background text-foreground rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const selectCls = 'border border-input bg-background text-foreground rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer';
const btnPrimary = 'inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50';
const btnSecondary = 'inline-flex items-center px-4 py-2 border border-border text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted transition-colors disabled:opacity-50';
const btnDanger = 'inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-destructive hover:bg-destructive/90 transition-colors';
